/**
 * 本机设置（§11.10 / §11.8）。持久化到 localStorage。
 *
 * 隐私模式 / 配额耗尽时 localStorage 会直接抛异常，必须降级成内存态而不是白屏 —— 
 * 这条和 historyStore 对 IndexedDB 的要求是同一条。
 */

import { create } from 'zustand'

export type PeekMode = 'hold' | 'tap'
export type Handedness = 'right' | 'left'

export interface Settings {
  readonly sound: boolean
  /** 牌桌背景音乐独立于事件音，可单独关闭 */
  readonly music: boolean
  readonly haptics: boolean
  /** 四色牌默认开：小屏上新手辨识快得多 */
  readonly fourColorDeck: boolean
  readonly highContrast: boolean
  /** 决定 dock 主按钮行的左右镜像 */
  readonly handedness: Handedness
  /** 省电模式：stagger 减半、筹码精灵恒为 1、取消光晕脉冲 */
  readonly lite: boolean
  /** 本地传手机：hold = 按住才翻正面（默认，防走光） */
  readonly peekMode: PeekMode
  readonly assistEnabled: boolean
  /** 统计页是否把本地对局算进 VPIP/PFR，默认关（§11.8） */
  readonly includeLocalHandsInStats: boolean
  readonly onboardingDone: boolean
  /** 牌桌上冒聊天气泡（关掉后只留未读数与抽屉里的历史） */
  readonly chatBubbles: boolean
}

export const DEFAULT_SETTINGS: Settings = {
  sound: true,
  music: true,
  haptics: true,
  fourColorDeck: true,
  highContrast: false,
  handedness: 'right',
  lite: false,
  peekMode: 'hold',
  assistEnabled: true,
  includeLocalHandsInStats: false,
  onboardingDone: false,
  chatBubbles: true,
}

const STORAGE_KEY = 'poker.settings.v1'

function readStored(): Partial<Settings> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    return parsed as Partial<Settings>
  } catch {
    // 隐私模式 / 配额耗尽 / 损坏的 JSON —— 一律当作没存过
    return {}
  }
}

function writeStored(s: Settings): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch {
    // 存不下就只保留本次会话，不打断牌局
  }
}

/**
 * 把设置投影到 documentElement 的 data-* 上，让 tokens.css 自己切变量。
 * 组件因此不需要任何 `if (fourColorDeck)` 分支。
 */
export function applySettingsToDom(s: Settings): void {
  const el = globalThis.document?.documentElement
  if (!el) return
  el.dataset['deck'] = s.fourColorDeck ? 'four' : 'two'
  el.dataset['contrast'] = s.highContrast ? 'high' : 'normal'
  el.dataset['lite'] = s.lite ? 'on' : 'off'
  el.dataset['hand'] = s.handedness
}

export interface SettingsStore extends Settings {
  set<K extends keyof Settings>(key: K, value: Settings[K]): void
  patch(partial: Partial<Settings>): void
  reset(): void
}

export const useSettingsStore = create<SettingsStore>((set, get) => {
  const initial: Settings = { ...DEFAULT_SETTINGS, ...readStored() }

  const commit = (next: Settings): void => {
    writeStored(next)
    applySettingsToDom(next)
    set(next)
  }

  const snapshot = (): Settings => {
    const s = get()
    return {
      sound: s.sound,
      music: s.music,
      haptics: s.haptics,
      fourColorDeck: s.fourColorDeck,
      highContrast: s.highContrast,
      handedness: s.handedness,
      lite: s.lite,
      peekMode: s.peekMode,
      assistEnabled: s.assistEnabled,
      includeLocalHandsInStats: s.includeLocalHandsInStats,
      onboardingDone: s.onboardingDone,
      chatBubbles: s.chatBubbles,
    }
  }

  return {
    ...initial,
    set: (key, value) => commit({ ...snapshot(), [key]: value }),
    patch: (partial) => commit({ ...snapshot(), ...partial }),
    reset: () => commit({ ...DEFAULT_SETTINGS }),
  }
})
